import { Slider as BaseSlider } from "@base-ui/react/slider";
import type { SliderRoot } from "@base-ui/react/slider";
import cx from "ui/cx";
import styles from "./Slider.module.scss";

export interface SliderProps extends SliderRoot.Props<number[]> {
  className?: string;
  /**
   * Accessible name for each thumb's input, keyed by thumb index (a range slider renders one
   * thumb per value). A root-level `aria-label` names the slider group, not the inputs; a
   * single-thumb slider reuses it for its input, but multi-thumb sliders should label their
   * endpoints here (e.g. "Minimum" / "Maximum").
   */
  getThumbAriaLabel?: (index: number) => string;
}

/**
 * Base UI's parts disagree on the emitted value shape for a one-element array value: the root
 * treats any array value as a range (keyboard edits emit `number[]`), while the control and
 * thumb key on `values.length > 1` (pointer presses emit a bare number). Consumers type against
 * `number[]` (see `SliderProps`), so a scalar leaks through paths like `value[0]` as `undefined`
 * and the update gets dropped — pointer input then looks dead while arrows still work.
 */
function toValueArray(value: number | readonly number[]): number[] {
  return typeof value === "number" ? [value] : [...value];
}

export default function Slider({
  className,
  getThumbAriaLabel,
  onValueChange,
  onValueCommitted,
  "aria-label": ariaLabel,
  ...props
}: SliderProps) {
  // One Thumb per value: Base UI does not spawn thumbs from the value array, so a range slider
  // (value={[min, max]}) needs a Thumb rendered for each entry to make both ends draggable.
  const thumbCount = Math.max(1, props.value?.length ?? props.defaultValue?.length ?? 1);

  // Base UI puts the root `aria-label` on the group element, not the focusable thumb inputs.
  // A single thumb's input is unambiguously named by the group label, so reuse it there;
  // sharing one label across multiple thumbs would leave the endpoints indistinguishable.
  let getThumbLabel = getThumbAriaLabel;
  if (getThumbLabel === undefined && thumbCount === 1 && ariaLabel !== undefined) {
    getThumbLabel = () => ariaLabel;
  }

  return (
    <BaseSlider.Root
      className={cx(styles.root, className)}
      data-testid="slider-root"
      aria-label={ariaLabel}
      onValueChange={onValueChange ? (value, details) => onValueChange(toValueArray(value), details) : undefined}
      onValueCommitted={
        onValueCommitted ? (value, details) => onValueCommitted(toValueArray(value), details) : undefined
      }
      {...props}
    >
      <BaseSlider.Control className={styles.control} data-testid="slider-control">
        <BaseSlider.Track className={styles.track} data-testid="slider-track">
          <BaseSlider.Indicator className={styles.indicator} data-testid="slider-indicator" />
          {Array.from({ length: thumbCount }, (_, index) => (
            <BaseSlider.Thumb
              key={index}
              index={index}
              getAriaLabel={getThumbLabel}
              className={styles.thumb}
              data-testid="slider-thumb"
            />
          ))}
        </BaseSlider.Track>
      </BaseSlider.Control>
    </BaseSlider.Root>
  );
}
